import { useCallback, useEffect, useMemo, useState } from 'react';

import { pushQueueApi } from '../../api/pushQueue.js';
import { billingApi } from '../../api/billing.js';
import { exportApi } from '../../api/export.js';
import { ExportCsvButton } from '../../components/ui/ExportCsvButton.jsx';
import { SearchableSelect } from '../../components/ui/SearchableSelect.jsx';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useEffectiveDateRange } from '../../hooks/useEffectiveDateRange.js';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll.js';

const POLL_MS = 5000;

const SORT_OPTIONS = [
  { value: 'created_at|desc',         label: 'Most recently queued' },
  { value: 'created_at|asc',          label: 'Oldest queued first' },
  { value: 'last_attempt_at|desc',    label: 'Most recently attempted' },
  { value: 'business_date|desc',      label: 'Newest punch date' },
  { value: 'business_date|asc',       label: 'Oldest punch date' },
  { value: 'employee_name|asc',       label: 'Employee A→Z' },
  { value: 'ns_location_name|asc',    label: 'Site A→Z' },
  { value: 'status|asc',              label: 'Status A→Z' },
];

export function PushQueuePanel() {
  // Filters. Default = all statuses so the tab is a complete history
  // view by default; user can narrow with the Status dropdown.
  const [status,         setStatus]         = useState('');
  const [search,         setSearch]         = useState('');
  const [dateStart,      setDateStart]      = useState('');
  const [dateEnd,        setDateEnd]        = useState('');
  const [siteCode,       setSiteCode]       = useState('');
  const [billingTitleId, setBillingTitleId] = useState('');
  const [sortKey,        setSortKey]        = useState('created_at|desc');
  const [facets,         setFacets]         = useState(null);

  const debouncedSearch = useDebouncedValue(search, 250);
  // Debounced + validated so half-typed dates don't spam/500 the backend.
  const [effDateStart, effDateEnd] = useEffectiveDateRange(dateStart, dateEnd);

  // Dates commit on blur (not per keystroke): the inputs are driven by local
  // drafts and only update the committed dateStart/dateEnd (which drive the
  // fetch) when the field loses focus or Enter is pressed. Drafts re-sync when
  // the committed value changes externally (Clear All).
  const [draftStart, setDraftStart] = useState(dateStart);
  const [draftEnd,   setDraftEnd]   = useState(dateEnd);
  useEffect(() => { setDraftStart(dateStart); }, [dateStart]);
  useEffect(() => { setDraftEnd(dateEnd); }, [dateEnd]);
  const commitStart = () => { if (draftStart !== dateStart) setDateStart(draftStart); };
  const commitEnd   = () => { if (draftEnd !== dateEnd) setDateEnd(draftEnd); };

  // THE filter payload. One builder feeds the table AND the CSV export, so the
  // file can never be narrower (or wider) than what was on screen. Paging is
  // deliberately not here: it changes what you SEE, never what MATCHES.
  const queryParams = useCallback(() => {
    const [sortBy, sortDir] = sortKey.split('|');
    const params = { sort_by: sortBy, sort_dir: sortDir };
    if (status)          params.status            = status;
    if (debouncedSearch) params.search            = debouncedSearch;
    if (effDateStart)    params.date_start        = effDateStart;
    if (effDateEnd)      params.date_end          = effDateEnd;
    if (siteCode)        params.site_code         = siteCode;
    if (billingTitleId)  params.billing_title_id  = billingTitleId;
    return params;
  }, [status, debouncedSearch, effDateStart, effDateEnd, siteCode, billingTitleId, sortKey]);

  // Build the page-fetch fn the infinite-scroll hook calls.
  const fetchPage = useCallback(
    (offset, limit) => pushQueueApi.list({ ...queryParams(), limit, offset }),
    [queryParams],
  );

  const {
    items, total, loading, loadingMore, hasMore,
    sentinelRef, reset,
  } = useInfiniteScroll(fetchPage, [fetchPage], { pageSize: 100 });

  // Load facets once for the Location + Billing Title selects.
  useEffect(() => {
    billingApi.facets({}).then(setFacets).catch(() => {});
  }, []);

  // Live poll: while any row is queued/in_progress, refresh the first page
  // so the UI reflects worker progress.
  const hasActive = useMemo(
    () => items.some((q) => q.status === 'queued' || q.status === 'in_progress'),
    [items],
  );
  useEffect(() => {
    if (!hasActive) return undefined;
    const id = setInterval(() => reset(), POLL_MS);
    return () => clearInterval(id);
  }, [hasActive, reset]);

  const hasAnyActive = !!(status || search || dateStart || dateEnd || siteCode || billingTitleId);
  const clearAll = () => {
    setStatus(''); setSearch('');
    setDateStart(''); setDateEnd('');
    setSiteCode(''); setBillingTitleId('');
  };

  return (
    <div>
      <div className="section-title" style={{ marginBottom: 8 }}>
        NetSuite Sync Queue ({total.toLocaleString()})
        {hasActive && (
          <span style={{ marginLeft: 10, fontSize: 11, color: '#0a58ca', fontWeight: 'normal' }}>
            ● live
          </span>
        )}
      </div>

      {/* Filter row 1 — search + dates */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 8,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Search</label>
          <input
            type="text" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Employee, site, punch ID, NS TimeBill ID…"
            style={{ width: 260 }}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Date From</label>
          <input
            type="date" value={draftStart}
            onChange={(e) => setDraftStart(e.target.value)}
            onBlur={commitStart}
            onKeyDown={(e) => { if (e.key === 'Enter') commitStart(); }}
            style={{ width: 150 }}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Date To</label>
          <input
            type="date" value={draftEnd}
            onChange={(e) => setDraftEnd(e.target.value)}
            onBlur={commitEnd}
            onKeyDown={(e) => { if (e.key === 'Enter') commitEnd(); }}
            style={{ width: 150 }}
          />
        </div>
        {hasAnyActive && (
          <button className="btn btn-primary" onClick={clearAll} style={{ alignSelf: 'flex-end' }}>
            Clear All
          </button>
        )}
        <ExportCsvButton
          onExport={() => exportApi.download('push_queue', { params: queryParams() })}
          disabled={loading || total === 0}
          title="Download every queue row matching these filters as CSV — not just the rows loaded here"
          style={{ alignSelf: 'flex-end', paddingBottom: 2 }}
        />
        <div style={{ fontSize: 12, color: '#666', alignSelf: 'flex-end', paddingBottom: 4 }}>
          Showing {items.length} of {total.toLocaleString()}
        </div>
      </div>

      {/* Filter row 2 — location + title + status + sort */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 16,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Location</label>
          <SearchableSelect
            value={siteCode}
            onChange={setSiteCode}
            options={(facets?.sites || []).map((s) => ({
              value: s.site_code, label: s.name,
            }))}
            placeholder="All locations — type to search"
            width={280}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Billing Title</label>
          <SearchableSelect
            value={billingTitleId}
            onChange={setBillingTitleId}
            options={(facets?.billing_titles || []).map((t) => ({
              value: t.id, label: t.name,
            }))}
            placeholder="All titles — type to search"
            width={240}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Status</label>
          <select
            value={status} onChange={(e) => setStatus(e.target.value)}
            style={{ width: 200 }}
          >
            <option value="">All statuses</option>
            <option value="queued,in_progress,failed">Active (queued + failed)</option>
            <option value="queued">Queued</option>
            <option value="in_progress">In progress</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="voided">Voided</option>
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Sort By</label>
          <select
            value={sortKey} onChange={(e) => setSortKey(e.target.value)}
            style={{ width: 220 }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
        Use the <strong>Synced</strong> tab to void records, or the <strong>Failed</strong> tab to retry.
      </div>

      {loading && items.length === 0 ? (
        <div className="alert alert-info">Loading…</div>
      ) : items.length === 0 ? (
        <div className="alert alert-info">No items in sync queue match the current filters.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>ID</th><th>Punch</th><th>Employee</th><th>Date</th>
                <th>Location</th><th>Title</th>
                <th>Pay Cat</th><th>Pay Code</th>
                <th style={{ textAlign: 'right' }}>Hours</th>
                <th style={{ textAlign: 'right' }}>Rate</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Status</th><th>NS TimeBill</th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.id}>
                  <td>{q.id}</td>
                  <td>{q.punch_xref_code}</td>
                  <td>{q.employee_name}</td>
                  <td>{q.business_date}</td>
                  <td>{q.ns_location_name || q.site_code}</td>
                  <td>{q.billing_title_name || '-'}</td>
                  <td>{q.pay_category || '-'}</td>
                  <td>{q.pay_code || '-'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {q.line_hours != null ? `${q.line_hours}h` : `${q.gross_hours}h`}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {q.line_rate != null ? `$${q.line_rate}` : '-'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {q.line_amount != null ? `$${q.line_amount}` : '-'}
                  </td>
                  <td>
                    <span className={`badge ${q.status === 'success' ? 'pushed' : q.status}`}>
                      {q.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: '#555' }}>
                    {q.ns_timebill_id || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div ref={sentinelRef} style={{ height: 1 }} />
          {loadingMore && (
            <div style={{ textAlign: 'center', padding: 12, color: '#666', fontSize: 13 }}>
              Loading more…
            </div>
          )}
          {!hasMore && items.length > 0 && (
            <div style={{ textAlign: 'center', padding: 12, color: '#999', fontSize: 12 }}>
              All {total.toLocaleString()} queue rows loaded
            </div>
          )}
        </div>
      )}
    </div>
  );
}
